import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Toast, ToastMessage } from "primereact/toast";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import desiriUserService from "../../services/desiriUser/userservice";

interface Props {
  onSearch: (text: string) => void;
  onRefresh: () => void;
}

const DesiriUserSearch: React.FC<Props> = ({ onSearch, onRefresh }) => {
  const { t } = useTranslation();
  const toast = useRef<Toast>(null);
  const [searchText, setSearchText] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [designationOptions, setDesignationOptions] = useState<
    { label: string; value: number }[]
  >([]);
  const [MUOptions, setMUOptions] = useState<
    { label: string; value: number }[]
  >([]);
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});

  const [hoveredField, setHoveredField] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    addressline1: "",
    addressline2: "",
    govtIDName: "",
    govtIDNumber: "",
    dob: null as Date | null,
    primaryContactNbr: "",
    secondaryContactNbr: "",
    designationID: null,
    gender: "",
    posID: null,
    esiNumber: "",
    pfNumber: "",
    bankNumber: "",
  });
  const [error, setError] = useState("");

  const genderOptions = [
    { label: "Male", value: "Male" },
    { label: "Female", value: "Female" },
  ];

  const govtIDNameOptions = [
    { label: "PAN", value: "PAN" },
    { label: "Aadhaar", value: "Aadhaar" },
    { label: "Others", value: "Others" },
  ];

  const showMessage = (
    label: string,
    message: string,
    severity: ToastMessage["severity"]
  ) => {
    toast.current?.show({
      severity,
      summary: label,
      detail: message,
      life: 3000,
    });
  };

  const handleSearch = () => {
    onSearch(searchText.trim());
  };

  const resetFields = () => {
    setSearchText("");
    onSearch("");
  };

  const handleSave = async () => {
    const validateForm = () => {
      const errors: { [key: string]: string } = {};

      if (!formData.name.trim()) errors.name = "Name is required";
      if (!formData.primaryContactNbr.trim())
        errors.primaryContactNbr = "Primary contact is required";
      if (!formData.designationID)
        errors.designationID = "Designation is required";
      if (!formData.gender) errors.gender = "Gender is required";
      if (!formData.dob) errors.dob = "Date of Birth is required";
      if (!formData.posID) errors.posID = "POS Unit is required";
      // if (!formData.govtIDName.trim())
      //   errors.govtIDName = "ID Name is required";
      // if (!formData.govtIDNumber.trim())
      //   errors.govtIDNumber = "ID Number is required";
      if (!formData.addressline1.trim())
        errors.addressline1 = "Address Line 1 is required";
      // if (!formData.esiNumber.trim())
      //   errors.esiNumber = "ESI Number is required";
      //  if (!formData.pfNumber.trim())
      //   errors.pfNumber = "PF Number is required";
      //  if (!formData.bankNumber.trim())
      //   errors.bankNumber = "Bank Number is required";

      setFormErrors(errors);
      return Object.keys(errors).length === 0;
    };

    if (!validateForm()) return;

    const {
      name,
      addressline1,
      addressline2,
      govtIDName,
      govtIDNumber,
      dob,
      primaryContactNbr,
      secondaryContactNbr,
      designationID,
      gender,
      posID,
      esiNumber,
      pfNumber,
      bankNumber,
    } = formData;

    const payload = {
      name,
      addressline1,
      addressline2,
      govtIDName,
      govtIDNumber,
      dob: dob ? new Date(dob).toLocaleDateString("en-CA") : null,
      primaryContactNbr,
      secondaryContactNbr,
      designationID,
      gender,
      posID,
      esiNumber,
      pfNumber,
      bankNumber,
    };

    try {
      const res = await desiriUserService.addUser(payload);

      if (res?.isSuccess) {
        showMessage(
          "Success",
          res.message || "User added successfully!",
          "success"
        );
        setShowAddModal(false);
        onRefresh();

        setFormData({
          name: "",
          addressline1: "",
          addressline2: "",
          govtIDName: "",
          govtIDNumber: "",
          dob: null,
          primaryContactNbr: "",
          secondaryContactNbr: "",
          designationID: null,
          gender: "",
          posID: null,
          esiNumber: "",
          pfNumber: "",
          bankNumber: "",
        });
        setFormErrors({});
      } else {
        showMessage("Failed", res?.message || "Failed to add user", "error");
      }
    } catch (err) {
      console.error("Error adding user:", err);
      showMessage("Error", "Error while adding user", "error");
    }
  };

  useEffect(() => {
    const fetchDesignations = async () => {
      try {
        const response = await desiriUserService.getAllDesignations();
        const formatted = response.map((d: any) => ({
          label: d.name,
          value: d.id,
        }));
        setDesignationOptions(formatted);
      } catch (error) {
        console.error("Failed to load designations:", error);
      }
    };

    fetchDesignations();
  }, []);

  useEffect(() => {
    const fetchMUPlants = async () => {
      try {
        const response = await desiriUserService.getAllMUPlants1();
        const formatted = response.map((d: any) => ({
          label: `${d.posCode} - ${d.posName}`,
          value: d.posID,
        }));

        setMUOptions(formatted);
      } catch (error) {
        console.error("Failed to load Manufacture Plants:", error);
      }
    };

    fetchMUPlants();
  }, []);

  const fieldHoverStyle = (fieldName: string) => ({
    border:
      hoveredField === fieldName ? "2px solid #6366F1" : "1px solid #ced4da",

    boxShadow:
      hoveredField === fieldName ? "0 0 6px rgba(99, 102, 241, 0.3)" : "none",
    transition: "all 0.2s ease-in-out",
    width: "100%",
    minHeight: "40px",
  });

  return (
    <div className="col-12">
      <div className="card border-noround p-2" style={{ minHeight: "3.5rem" }}>
        <div
          className="input-container font-bold text-white border-round"
          style={{ float: "left", width: "18%" }}
        >
          <div className="p-input-icon-left">
            <i className="pi pi-search" />
            <InputText
              type="search"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Keyword Search"
              style={{ width: "100%" }}
            />
          </div>
        </div>

        <div className="buttons-container">
          <div
            className="filter-button font-bold text-white border-round"
            style={{ float: "left", width: "10%" }}
          >
            <Button
              className="text-black border-round"
              style={{ width: "100%" }}
              onClick={handleSearch}
              severity="success"
            >
              Filter
            </Button>
          </div>
          <div
            className="filter-button font-bold text-white border-round ml-2"
            style={{ float: "left", width: "10%" }}
          >
            <Button
              className="text-black border-round"
              style={{ width: "100%" }}
              onClick={resetFields}
              severity="secondary"
              text
              raised
            >
              Reset
            </Button>
          </div>
          <div
            className="add-button-container border-round font-bold ml-3 text-primary"
            style={{ textAlign: "right" }}
          >
            <Button
              className="bg-blue-700 border-round p-button p-button-text p-component text-white"
              onClick={() => setShowAddModal(true)}
              severity="secondary"
              text
              raised
            >
              Add User
            </Button>
          </div>
        </div>
      </div>

      <Dialog
        visible={showAddModal}
        className="quickview w-50"
        breakpoints={{ "960px": "75vw", "641px": "90vw" }}
        modal
        onHide={() => {
          setShowAddModal(false);
          setFormData({
            name: "",
            addressline1: "",
            addressline2: "",
            govtIDName: "",
            govtIDNumber: "",
            dob: null,
            primaryContactNbr: "",
            secondaryContactNbr: "",
            designationID: null,
            gender: "",
            posID: null,
            esiNumber: "",
            pfNumber: "",
            bankNumber: "",
          });
          setError("");
        }}
      >
        <h5 className="border-round mb-3 order-top-color p-2 panel-header-text text-center">
          ADD USER
        </h5>
        <div className="grid">
          <div className="col-12 mb-2">
            <div className="grid">
              {/* Name */}
              <div className="field col-12 md:col-6">
                <label className="label-semi-bold">
                  Name<span className="required">*</span>
                </label>
                <InputText
                  value={formData.name}
                  onChange={(e) => {
                    setFormData({ ...formData, name: e.target.value });
                    if (formErrors.name) {
                      setFormErrors({ ...formErrors, name: "" });
                    }
                  }}
                  style={fieldHoverStyle("description")}
                  placeholder="Name"
                />
                {formErrors.name && (
                  <small className="p-error">{formErrors.name}</small>
                )}
              </div>

              {/* Manufacturing Unit */}
              <div className="field col-12 md:col-6">
                <label className="label-semi-bold">
                  POS<span className="required">*</span>
                </label>
                <Dropdown
                  value={formData.posID}
                  options={MUOptions}
                  onChange={(e) => {
                    setFormData({ ...formData, posID: e.value });
                    if (formErrors.posID)
                      setFormErrors({ ...formErrors, posID: "" });
                  }}
                  placeholder="Select POS"
                  style={fieldHoverStyle("posID")}
                />
                {formErrors.posID && (
                  <small className="p-error">{formErrors.posID}</small>
                )}
              </div>

              {/* Primary Contact */}
              <div className="field col-12 md:col-6">
                <label className="label-semi-bold">
                  Primary Contact<span className="required">*</span>
                </label>
                <InputText
                  value={formData.primaryContactNbr}
                  onChange={(e) => {
                    const numeric = e.target.value.replace(/\D/g, "");
                    if (numeric.length <= 10) {
                      setFormData({ ...formData, primaryContactNbr: numeric });
                    }
                  }}
                  style={fieldHoverStyle("description")}
                  placeholder="Mobile Number"
                />
                {formErrors.primaryContactNbr && (
                  <small className="p-error">
                    {formErrors.primaryContactNbr}
                  </small>
                )}
              </div>

              {/* Secondary Contact */}
              <div className="field col-12 md:col-6">
                <label className="label-semi-bold">Secondary Contact</label>
                <InputText
                  value={formData.secondaryContactNbr}
                  onChange={(e) => {
                    const numeric = e.target.value.replace(/\D/g, "");
                    if (numeric.length <= 10) {
                      setFormData({
                        ...formData,
                        secondaryContactNbr: numeric,
                      });
                    }
                  }}
                  style={fieldHoverStyle("description")}
                  placeholder="Alternate Mobile Number"
                />
              </div>

              {/* Designation */}
              <div className="field col-12 md:col-6">
                <label className="label-semi-bold">
                  Designation<span className="required">*</span>
                </label>
                <Dropdown
                  value={formData.designationID}
                  options={designationOptions}
                  onChange={(e) => {
                    setFormData({ ...formData, designationID: e.value });
                    if (formErrors.designationID)
                      setFormErrors({ ...formErrors, designationID: "" });
                  }}
                  filter
                  placeholder="Select Designation"
                  style={fieldHoverStyle("designationID")}
                />

                {formErrors.designationID && (
                  <small className="p-error">{formErrors.designationID}</small>
                )}
              </div>

              {/* Gender */}
              <div className="field col-12 md:col-6">
                <label className="label-semi-bold">
                  Gender<span className="required">*</span>
                </label>
                <Dropdown
                  value={formData.gender}
                  options={genderOptions}
                  onChange={(e) => {
                    setFormData({ ...formData, gender: e.value });
                    if (formErrors.gender)
                      setFormErrors({ ...formErrors, gender: "" });
                  }}
                  placeholder="Select Gender"
                  style={fieldHoverStyle("gender")}
                />
                {formErrors.gender && (
                  <small className="p-error">{formErrors.gender}</small>
                )}
              </div>

              {/* DOB */}
              <div className="field col-12 md:col-6">
                <label className="label-semi-bold">
                  Date of Birth<span className="required">*</span>
                </label>
                <Calendar
                  value={formData.dob}
                  onChange={(e) => {
                    setFormData({ ...formData, dob: e.value as Date });
                    if (formErrors.dob)
                      setFormErrors({ ...formErrors, dob: "" });
                  }}
                  dateFormat="dd-mm-yy"
                  style={fieldHoverStyle("dob")}
                  placeholder="Select DOB"
                />
                {formErrors.dob && (
                  <small className="p-error">{formErrors.dob}</small>
                )}
              </div>

              {/* Govt ID Name */}
              <div className="field col-12 md:col-6">
                <label className="label-semi-bold">
                  Govt ID Name
                  {/* <span className="required">*</span> */}
                </label>

                <Dropdown
                  value={formData.govtIDName}
                  options={govtIDNameOptions}
                  onChange={(e) => {
                    setFormData({ ...formData, govtIDName: e.value });
                    if (formErrors.govtIDName)
                      setFormErrors({ ...formErrors, govtIDName: "" });
                  }}
                  placeholder="Select Govt ID Name"
                  style={fieldHoverStyle("govtIDName")}
                />
                {formErrors.govtIDName && (
                  <small className="p-error">{formErrors.govtIDName}</small>
                )}
              </div>

              <div className="field col-12 md:col-6">
                <label className="label-semi-bold">
                  Govt ID Number
                  {/* <span className="required">*</span> */}
                </label>
                <InputText
                  value={formData.govtIDNumber}
                  onChange={(e) => {
                    setFormData({ ...formData, govtIDNumber: e.target.value });
                    if (formErrors.govtIDNumber)
                      setFormErrors({ ...formErrors, govtIDNumber: "" });
                  }}
                  style={fieldHoverStyle("govtIDNumber")}
                  placeholder="Enter ID Number"
                  maxLength={12}
                />
                {formErrors.govtIDNumber && (
                  <small className="p-error">{formErrors.govtIDNumber}</small>
                )}
              </div>

              {/* Address Line 1 */}
              <div className="field col-12 md:col-6">
                <label className="label-semi-bold">
                  Address Line 1<span className="required">*</span>
                </label>
                <InputText
                  value={formData.addressline1}
                  onChange={(e) => {
                    setFormData({ ...formData, addressline1: e.target.value });
                    if (formErrors.addressline1)
                      setFormErrors({ ...formErrors, addressline1: "" });
                  }}
                  style={fieldHoverStyle("addressline1")}
                  placeholder="House No / Street"
                />
                {formErrors.addressline1 && (
                  <small className="p-error">{formErrors.addressline1}</small>
                )}
              </div>

              {/* Address Line 2 */}
              <div className="field col-12 md:col-6">
                <label className="label-semi-bold">Address Line 2</label>
                <InputText
                  value={formData.addressline2}
                  onChange={(e) => {
                    setFormData({ ...formData, addressline2: e.target.value });
                    if (formErrors.addressline2)
                      setFormErrors({ ...formErrors, addressline2: "" });
                  }}
                  style={fieldHoverStyle("addressline2")}
                  placeholder="Landmark / Area"
                />
                {formErrors.addressline2 && (
                  <small className="p-error">{formErrors.addressline2}</small>
                )}
              </div>

              <div className="field col-12 md:col-6">
                <label className="label-semi-bold">
                  ESI Number
                  {/* <span className="required">*</span> */}
                </label>
                <InputText
                  value={formData.esiNumber}
                  onChange={(e) => {
                    setFormData({ ...formData, esiNumber: e.target.value });
                    if (formErrors.esiNumber)
                      setFormErrors({ ...formErrors, esiNumber: "" });
                  }}
                  style={fieldHoverStyle("esiNumber")}
                  placeholder="Enter ESI Number"
                  maxLength={12}
                />
                {formErrors.esiNumber && (
                  <small className="p-error">{formErrors.esiNumber}</small>
                )}
              </div>
              <div className="field col-12 md:col-6">
                <label className="label-semi-bold">
                  PF Number
                  {/* <span className="required">*</span> */}
                </label>
                <InputText
                  value={formData.pfNumber}
                  onChange={(e) => {
                    setFormData({ ...formData, pfNumber: e.target.value });
                    if (formErrors.pfNumber)
                      setFormErrors({ ...formErrors, pfNumber: "" });
                  }}
                  style={fieldHoverStyle("pfNumber")}
                  placeholder="Enter PF Number"
                  maxLength={12}
                />
                {formErrors.pfNumber && (
                  <small className="p-error">{formErrors.pfNumber}</small>
                )}
              </div>
              <div className="field col-12 md:col-6">
                <label className="label-semi-bold">
                  Bank Number
                  {/* <span className="required">*</span> */}
                </label>
                <InputText
                  value={formData.bankNumber}
                  onChange={(e) => {
                    setFormData({ ...formData, bankNumber: e.target.value });
                    if (formErrors.bankNumber)
                      setFormErrors({ ...formErrors, bankNumber: "" });
                  }}
                  style={fieldHoverStyle("bankNumber")}
                  placeholder="Enter Bank Number"
                  maxLength={12}
                />
                {formErrors.bankNumber && (
                  <small className="p-error">{formErrors.bankNumber}</small>
                )}
              </div>

              <div className="field col-12 md:col-6 text-right  mt-5">
                <Button
                  className="bg-green-700 text-white p-button p-button-text p-component"
                  onClick={handleSave}
                >
                  Save
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Dialog>

      <Toast ref={toast} />
    </div>
  );
};

export default DesiriUserSearch;
